import { Modal, Spinner } from "react-bootstrap";
import { isMobile } from "react-device-detect";
import { useState, useEffect, FormEvent } from "react";
import { Link, useHistory } from "react-router-dom";
import { projectPrimaryColor, projectLogoLight } from "../../generalConfigs";
import validator from "validator";
import useAuth from "../../hooks/useAuth";
import useExternalAuth from "../../hooks/useExternalAuth";
import useStoredImage from "../../hooks/useStoredImage";
import PhoneConfirmationModal from "../../components/PhoneConfirmationModal";

export default function SignUp() {
  const [emailValue, setEmailValue] = useState("");
  const [usernameValue, setUsernameValue] = useState("");
  const [passwordValue, setPasswordValue] = useState("");
  const [confirmPasswordValue, setConfirmPasswordValue] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingExternalAuth, setIsLoadingExternalAuth] =
    useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModel] =
    useState<boolean>(false);

  const { isAuthenticated } = useAuth();
  const history = useHistory();
  const { getRedirectURL } = useExternalAuth();
  const { getCommonImageSrc } = useStoredImage();

  useEffect(() => {
    if (isAuthenticated()) {
      history.push("/dashboard");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg("");

    if (!validator.isEmail(emailValue)) {
      setErrorMsg("Verifique o campo 'Email'");
      setIsLoading(false);

      return;
    }

    if (passwordValue !== confirmPasswordValue) {
      setErrorMsg(
        "Sua confirmação de senha não coincide, verifique os campos e tente novamente."
      );
      setIsLoading(false);

      return;
    }

    await new Promise((res, rej) => {
      setTimeout(() => {
        setShowConfirmationModel(true);
        setIsLoading(false);
        res(true);
      }, 1500);
    });
  }

  async function handleRedirectExternalAuth(provider: string) {
    setIsLoadingExternalAuth(true);

    if (provider === "google") {
      const redirectUrl = await getRedirectURL("google");

      if (redirectUrl) {
        window.location.replace(redirectUrl);
      }
    }

    setIsLoadingExternalAuth(false);
  }

  if (showConfirmationModal) {
    return (
      <PhoneConfirmationModal
        email={emailValue}
        username={usernameValue}
        password={passwordValue}
        confirmPassword={confirmPasswordValue}
        show={showConfirmationModal}
        onClickBack={() => setShowConfirmationModel(false)}
      />
    );
  }

  return (
    <Modal size="lg" fullscreen={true} show={true}>
      <div className="bg-dark">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 shadow-lg">
              <div className="hero-wrap d-flex align-items-center rounded-3 rounded-end-0 h-100">
                <div className="hero-mask opacity-9 bg-primary" />
                <div className="hero-bg hero-bg-scroll" />
                <div className="hero-content mx-auto w-100 h-100 d-flex flex-column">
                  <div className="row g-0">
                    <div className="col-11 col-lg-10 mx-auto">
                      <div className="mt-3">{projectLogoLight}</div>
                    </div>
                  </div>
                  <div className={isMobile ? "row g-0 my-3" : "row g-0 my-4"}>
                    <div className="col-11 col-lg-10 mx-auto">
                      <h1 className="text-11" style={{ color: "#e0e0e0" }}>
                        Pronto para ser{" "}
                        <span className="markText-animation">Fato?</span>
                      </h1>
                      <p
                        className="text-5 lh-base mb-4"
                        style={{ color: "#e0e0e0" }}
                      >
                        Cadastre-se para ter acesso a melhor plataforma de
                        análise de investimentos baseada em machine learning.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="col-lg-6 shadow-lg d-flex align-items-center rounded-3 rounded-start-0"
              style={{
                backgroundColor: "white",
                borderRadius: 10,
              }}
            >
              {isLoading ? (
                loadingSkeleton()
              ) : (
                <div className="container my-auto py-4">
                  <div className="row">
                    <div className="col-11 col-lg-10 mx-auto">
                      <h3 className="text-black text-center mb-3 ">Cadastro</h3>

                      <form className="form" onSubmit={handleSubmit}>
                        <div className="mb-3">
                          <label className="form-label text-black">
                            E-mail
                          </label>
                          <input
                            type="email"
                            value={emailValue}
                            onChange={(e) =>
                              setEmailValue(e.target.value.toLowerCase())
                            }
                            className="form-control"
                            required
                            placeholder="Insira seu e-mail"
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label text-black">Nome</label>
                          <input
                            type="text"
                            value={usernameValue}
                            onChange={(e) => setUsernameValue(e.target.value)}
                            className="form-control"
                            required
                            minLength={5}
                            placeholder="Insira seu nome"
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label text-black">Senha</label>
                          <input
                            type="password"
                            value={passwordValue}
                            onChange={(e) => setPasswordValue(e.target.value)}
                            className="form-control"
                            minLength={7}
                            required
                            placeholder="Insira sua senha"
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label text-black">
                            Confirme sua senha
                          </label>
                          <input
                            type="password"
                            value={confirmPasswordValue}
                            minLength={7}
                            onChange={(e) =>
                              setConfirmPasswordValue(e.target.value)
                            }
                            className="form-control"
                            required
                            placeholder="Insira sua senha novamente"
                          />
                        </div>

                        <div className="row mt-4">
                          <div className="col">
                            <div className="form-check text-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                required
                              />
                              <label className="form-check-label text-black">
                                Declaro que li e concordo com os
                                <a
                                  target="_blank"
                                  className="mx-1"
                                  href="https://www.fatores.ai/terms/use"
                                  rel="noreferrer"
                                >
                                  termos e condições de uso
                                </a>
                                e
                                <a
                                  target="_blank"
                                  className="mx-1"
                                  href="https://www.fatores.ai/terms/privacy"
                                  rel="noreferrer"
                                >
                                  política de privacidade.
                                </a>
                              </label>
                            </div>
                          </div>
                        </div>
                        <div className="d-grid my-4 d-flex flex-column align-items-center">
                          {errorMsg !== "" && (
                            <span
                              className="my-2"
                              style={{
                                color: "red",
                                textAlign: "center",
                              }}
                            >
                              {errorMsg}
                            </span>
                          )}

                          <button
                            className="btn btn-primary"
                            style={{
                              borderRadius: 10,
                              width: "50%",
                              backgroundColor: projectPrimaryColor,
                            }}
                            type="submit"
                          >
                            Criar minha conta
                          </button>
                        </div>
                      </form>
                      <div className="d-flex align-items-center my-1">
                        <hr className="flex-grow-1 bg-dark-4" />
                        <span className="mx-2 text-2 text-muted">
                          Continue utilizando outras plataformas
                        </span>
                        <hr className="flex-grow-1 bg-dark-4" />
                      </div>
                      <div className="d-flex flex-column align-items-center mb-4">
                        {
                          // Loading external auth
                          isLoadingExternalAuth ? (
                            <button
                              className="btn btn-outline-black"
                              style={{
                                textTransform: "none",
                                borderRadius: 15,
                                color: "black",
                                borderColor: "#969dab",
                                cursor: "default",
                              }}
                            >
                              <Spinner animation="border" size="sm" />
                            </button>
                          ) : (
                            <button
                              className="btn btn-outline-black"
                              onClick={() =>
                                handleRedirectExternalAuth("google")
                              }
                              style={{
                                textTransform: "none",
                                borderRadius: 15,
                                color: "black",
                                borderColor: "#969dab",
                              }}
                            >
                              <img
                                width="20px"
                                style={{ marginBottom: 1, marginRight: 5 }}
                                alt="Google Sign-in"
                                src={getCommonImageSrc(
                                  "Google__G__Logo.svg.png"
                                )}
                              />
                              Continuar com Google
                            </button>
                          )
                        }
                      </div>
                      <p className="text-2 text-center text-black mb-0">
                        Já é Fato?
                        <span className="mx-1">
                          <Link to="/login">Faça login</Link>
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

const loadingSkeleton = () => {
  return (
    <div className="container my-auto py-4">
      <div className="row">
        <div className="col-11 col-lg-10 mx-auto">
          <h3
            className="text-black text-center mb-3"
            style={{ minHeight: "20vh" }}
          >
            <Spinner animation="border" style={{ marginTop: "20%" }} />
          </h3>
        </div>
      </div>
    </div>
  );
};
